/**
 * Permission primitives.
 *
 * Source of truth: docs/phase0/04-permissions-matrix.md
 *
 * This module and `matrix.ts` are the ONLY place the permission model is
 * expressed. The API middleware, the UI, and the generated permission test
 * suite all read from here — see ADR-019. If you find yourself writing a
 * role check anywhere else, that is a bug.
 */

/** The ten roles from `04-permissions-matrix.md`. */
export const ROLES = [
  'company_admin',
  'director',
  'project_manager',
  'design_coordinator',
  'procurement',
  'site_engineer',
  'qs_commercial',
  'qa_qc',
  'client_consultant',
  'subcontractor',
] as const;

export type Role = (typeof ROLES)[number];

/**
 * Roles held by parties outside the contracting company.
 *
 * These are subject to the hard rules in `04` §"External user rules", which are
 * enforced independently of — and in addition to — the matrix. Most importantly
 * they can NEVER hold `finance:view`, at the role level, so it cannot be
 * granted to them by a misconfigured membership.
 */
export const EXTERNAL_ROLES = ['client_consultant', 'subcontractor'] as const;
export type ExternalRole = (typeof EXTERNAL_ROLES)[number];

export function isExternalRole(role: Role): role is ExternalRole {
  return (EXTERNAL_ROLES as readonly string[]).includes(role);
}

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/**
 * What each role is called in front of a human.
 *
 * Here rather than in the UI because the API needs them too: system role rows
 * are seeded into every company from this list, and a company's role table
 * saying "qs_commercial" while the screen says "QS / Commercial" is the kind of
 * drift that ends with two names for one thing.
 */
export const ROLE_LABELS: Readonly<Record<Role, string>> = Object.freeze({
  company_admin: 'Company Admin',
  director: 'Owner / Director',
  project_manager: 'Project Manager',
  design_coordinator: 'Design Coordinator',
  procurement: 'Procurement',
  site_engineer: 'Site Engineer',
  qs_commercial: 'QS / Commercial',
  qa_qc: 'QA/QC',
  client_consultant: 'Client / Consultant',
  subcontractor: 'Subcontractor',
});

/** The 31 resources. */
export const RESOURCES = [
  // Identity & admin
  'company',
  'user',
  'role',
  'template',
  // Project structure
  'project',
  'stage',
  'milestone',
  'location',
  'team',
  // Work
  'task',
  // Design
  'deliverable',
  'submittal',
  // Supply
  'material',
  'supplier',
  'delivery',
  // Site
  'activity',
  'diary',
  'photo',
  // Quality
  'inspection',
  'ncr',
  'snag',
  'handover',
  // Commercial
  'budget',
  'cost',
  'variation',
  'billing',
  // Control
  'document',
  'report',
  'pulse',
  'audit',
  'ai',
] as const;

export type Resource = (typeof RESOURCES)[number];

/** Resources whose every field is financial. Gated wholesale by `finance:view`. */
export const FINANCIAL_RESOURCES = ['budget', 'cost', 'variation', 'billing'] as const;
export type FinancialResource = (typeof FINANCIAL_RESOURCES)[number];

export function isFinancialResource(resource: Resource): resource is FinancialResource {
  return (FINANCIAL_RESOURCES as readonly string[]).includes(resource);
}

export const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export', 'admin'] as const;
export type Action = (typeof ACTIONS)[number];

/**
 * Narrows a grant to a subset of records.
 *
 * `all`       — every record on the project
 * `own`       — records the user created or owns
 * `assigned`  — records assigned to the user (subcontractors, corrective actions)
 * `shared`    — only records/comments marked `visibility: shared`
 * `issued`    — only issued report versions, never drafts
 * `project`   — scoped to the user's own projects (used for `audit`)
 * `override`  — the edit is limited to the override action (used for `pulse`)
 */
export const SCOPES = ['all', 'own', 'assigned', 'shared', 'issued', 'project', 'override'] as const;
export type Scope = (typeof SCOPES)[number];

export interface Grant {
  readonly action: Action;
  readonly scope: Scope;
  /**
   * When true, this grant is additionally conditional on the `finance:view`
   * flag on the user's project membership. Corresponds to `V*` in the matrix.
   */
  readonly requiresFinance: boolean;
}

/** Every grant a role holds, by resource. A missing resource key means no access. */
export type RoleGrants = Readonly<Partial<Record<Resource, readonly Grant[]>>>;

export type PermissionMatrix = Readonly<Record<Role, RoleGrants>>;

// ---------------------------------------------------------------------------
// Builders — keep `matrix.ts` readable enough to diff against the markdown.
// ---------------------------------------------------------------------------

const LETTER_TO_ACTION: Readonly<Record<string, Action>> = {
  V: 'view',
  C: 'create',
  E: 'edit',
  D: 'delete',
  A: 'approve',
  X: 'export',
  M: 'admin', // 'M' for aDMin — 'A' is already approve
};

function build(letters: string, scope: Scope, requiresFinance: boolean): readonly Grant[] {
  return Object.freeze(
    [...letters].map((letter) => {
      const action = LETTER_TO_ACTION[letter];
      if (!action) {
        throw new Error(
          `Unknown permission letter "${letter}". Legal letters: ${Object.keys(LETTER_TO_ACTION).join(', ')}`,
        );
      }
      return Object.freeze({ action, scope, requiresFinance });
    }),
  );
}

/** Grants over every record on the project. `g('VCED')` */
export function g(letters: string): readonly Grant[] {
  return build(letters, 'all', false);
}

/** Grants limited to a scope. `scoped('VE', 'assigned')` */
export function scoped(letters: string, scope: Scope): readonly Grant[] {
  return build(letters, scope, false);
}

/** Grants additionally requiring `finance:view`. Matrix notation `V*`. */
export function fin(letters: string): readonly Grant[] {
  return build(letters, 'all', true);
}

/** Concatenate grant lists for a resource that mixes scopes. */
export function merge(...groups: readonly (readonly Grant[])[]): readonly Grant[] {
  return Object.freeze(groups.flat());
}
